<template>
    <section class="ebc-panel capacity-panel">
        <div class="ebc-panel__head">
            <div>
                <h3 class="ebc-panel__title">계약전력 최적화</h3>
                <div v-if="capacity && capacity.applicable" class="ebc-segmented capacity-views">
                    <button
                        v-for="view in views"
                        :key="view.key"
                        type="button"
                        class="ebc-segmented__item"
                        :class="{ active: chartView === view.key }"
                        @click="chartView = view.key"
                    >
                        {{ view.label }}
                    </button>
                </div>
            </div>
            <div v-if="capacity && capacity.applicable" class="capacity-badges">
                <span v-if="lowConfidence" v-b-tooltip.hover="tip(coverageTitle)" class="ebc-badge is-warn">
                    <i :class="verdict.icon"></i>{{ verdict.text }} · 근거 부족 {{ coverage.months }}/{{
                        coverage.total
                    }}개월
                </span>
                <template v-else>
                    <span v-if="coverage" v-b-tooltip.hover="tip(coverageTitle)" class="ebc-badge">
                        근거 {{ coverage.months }}/{{ coverage.total }}개월
                    </span>
                    <span class="ebc-badge" :class="verdict.cls">
                        <i :class="verdict.icon"></i>{{ verdict.text }}
                    </span>
                </template>
                <i v-b-tooltip.hover="tip(notesTitle)" class="fas fa-info-circle capacity-info"></i>
            </div>
        </div>

        <div v-if="!capacity || !capacity.applicable" class="ebc-panel__body">
            <div class="ebc-empty">
                <i class="fas fa-bolt"></i>
                <span>{{ capacity ? capacity.message : "추천 계약전력 정보가 없습니다" }}</span>
            </div>
        </div>

        <div v-else class="ebc-panel__body capacity-body">
            <div class="capacity-chart">
                <EChart :option="chartView === 'curve' ? curveOption : riskOption" @chart-click="onChartClick" />
            </div>

            <div class="sim-card">
                <div class="preset-grid">
                    <button
                        v-for="p in presetCards"
                        :key="p.key"
                        type="button"
                        class="preset ebc-num"
                        :class="{ 'is-active': p.capacity === selectedCapacity }"
                        @click="selectCapacity(p.capacity)"
                    >
                        <span class="preset__label">
                            {{ p.label }}<em v-if="p.key === 'recommended'" class="preset__tag">추천</em>
                        </span>
                        <span class="preset__value">{{ num(p.capacity) }}<small>kW</small></span>
                        <span class="preset__diff" :class="diffClass(diffOf(p))">
                            {{ p.key === "current" ? "기준" : `${periodPrefix} ${signedWonShort(diffOf(p))}` }}
                        </span>
                        <span v-if="riskOf(p)" class="preset__risk">{{ riskOf(p) }}</span>
                    </button>
                </div>

                <dl class="sim-metrics ebc-num">
                    <div>
                        <dt>{{ feeLabel }}</dt>
                        <dd>{{ wonShort(feeOf(selected)) }}원</dd>
                    </div>
                    <div>
                        <dt>{{ isCostUp ? "연 환산 증가" : "연 환산 절감" }}</dt>
                        <dd :class="diffClass(-selected.annualSaving)">
                            {{ isCostUp ? "+" : "" }}{{ wonShort(Math.abs(selected.annualSaving)) }}원
                            <span class="sim-rate">{{ pct(Math.abs(selected.savingRate)) }}</span>
                        </dd>
                    </div>
                    <div>
                        <dt>초과 위험</dt>
                        <dd :class="{ 'is-bad': selected.overrunMonths > 0 }">
                            <template v-if="selected.overrunMonths > 0">
                                {{ selected.overrunMonths }}개월
                                <span class="sim-rate">(최대 {{ pct(selected.maxOverrunRate) }})</span>
                            </template>
                            <template v-else>없음</template>
                        </dd>
                    </div>
                    <p v-if="penaltyNote" class="sim-metrics__note">{{ penaltyNote }}</p>
                </dl>

                <ul v-if="warnings.length" class="sim-insights">
                    <li v-for="w in warnings" :key="w.key" :class="`is-${w.level}`">
                        <div class="sim-insights__body">
                            <strong>
                                <em v-if="w.level !== 'info'" class="sim-insights__level">{{ levelText[w.level] }}</em
                                >{{ w.text }}
                            </strong>
                            <span v-if="w.detail">{{ w.detail }}</span>
                        </div>
                        <button
                            v-if="w.action"
                            type="button"
                            class="sim-insights__action ebc-num"
                            @click="selectCapacity(w.action.capacity)"
                        >
                            <b>{{ w.action.label }}</b>
                            <small v-if="w.action.sub">{{ w.action.sub }}</small>
                        </button>
                        <span v-else-if="w.value" class="sim-insights__value ebc-num">{{ w.value }}</span>
                    </li>
                </ul>
            </div>
        </div>
    </section>
</template>

<script>
import EChart from "./EChart.vue";
import { chartColors, grayScale, navyScale, barGradient, tooltipStyle, tooltipHtml } from "../../utils/chartTheme.js";
import { axisLabel, axisLine, splitLine, legendText } from "./analysisChart.js";
import { num, pct, wonShort, ymShort, ymLabel } from "./analysisFormat.js";

function niceCeil(value) {
    if (!(value > 0)) return 10;
    const pow = Math.pow(10, Math.floor(Math.log10(value)));
    const unit = value / pow;
    const nice =
        unit <= 1 ? 1 : unit <= 1.5 ? 1.5 : unit <= 2 ? 2 : unit <= 3 ? 3 : unit <= 5 ? 5 : unit <= 7.5 ? 7.5 : 10;
    return nice * pow;
}

const VERDICTS = {
    reduce: { text: "감액 검토", icon: "fas fa-arrow-down", cls: "is-positive" },
    increase: { text: "초과 위험", icon: "fas fa-exclamation-triangle", cls: "is-danger" },
    keep: { text: "적정", icon: "fas fa-check", cls: "is-primary" },
};

const PRESET_LABELS = {
    current: "현재 유지",
    safe: "안전 +20%",
    recommended: "권장 +10%",
    minCost: "최저 비용",
};

const PENALTY_COLOR = "rgba(194, 91, 91, 0.35)";
// 일별 추이 요금 막대(navy 2단계)와 구분: 수요 막대는 가장 진한 1단계, 선택 계약전력은 막대와 대비되는 밝은 3단계
const DEMAND_COLOR = navyScale[0];
const SELECTED_COLOR = navyScale[2];
const CURRENT_COLOR = grayScale[1];

export default {
    name: "CapacityOptimizerPanel",
    components: { EChart },
    props: {
        capacity: {
            type: Object,
            default: null,
        },
        analysisType: {
            type: String,
            default: "monthly",
        },
    },
    data() {
        return {
            selectedIndex: 0,
            levelText: { danger: "위험", warn: "주의" },
            chartView: "risk",
            views: [
                { key: "risk", label: "월별 위험" },
                { key: "curve", label: "요금 곡선" },
            ],
        };
    },
    computed: {
        scenarios() {
            return this.capacity?.scenarios || [];
        },
        selected() {
            return this.scenarios[this.selectedIndex] || this.capacity?.recommended || {};
        },
        isCostUp() {
            return this.selected.annualSaving < 0;
        },
        selectedCapacity() {
            return this.selected.capacity;
        },
        isMonthly() {
            return this.analysisType === "monthly";
        },
        refMonthNo() {
            return parseInt(String(this.capacity?.refYm || "").substring(4, 6), 10);
        },
        hasRefMonthFee() {
            return Number.isFinite(this.capacity?.current?.refMonthCost);
        },
        feeLabel() {
            if (!this.isMonthly) return "연 예상 부담";
            if (!this.hasRefMonthFee) return "월 평균 부담";
            return `${this.refMonthNo}월 예상 부담`;
        },
        periodPrefix() {
            return this.isMonthly ? "월" : "연";
        },
        lowConfidence() {
            return this.capacity?.confidence === "low";
        },
        verdict() {
            return VERDICTS[this.capacity?.verdict] || VERDICTS.keep;
        },
        coverage() {
            return this.capacity?.coverage || null;
        },
        outlier() {
            const o = this.capacity?.outlier;
            return o && o.suspected ? o : null;
        },
        tariffBoundary() {
            return this.capacity?.tariff?.boundary || null;
        },
        /** 이보다 낮춰도 요금이 그대로인 계약전력 (월별 보기는 기준월, 연간은 근거 월 전체) */
        knee() {
            const c = this.capacity;
            if (!c || c.calcType !== "PeakLinked") return null;
            return this.isMonthly && c.refMonthNoSavingBelow ? c.refMonthNoSavingBelow : c.noSavingBelow;
        },
        coverageTitle() {
            const cov = this.coverage;
            const missing = cov?.missing || [];
            const lines = [
                missing.length
                    ? `데이터 없는 달: ${missing.map((ym) => ymShort(ym, true)).join(", ")}`
                    : "최근 12개월 데이터 모두 있음",
            ];
            const seasons = [];
            const missingMonths = (cov?.missingSeason || []).map((ym) => parseInt(String(ym).substring(4, 6), 10));
            if (missingMonths.some((m) => [12, 1, 2].includes(m))) seasons.push("겨울");
            if (missingMonths.some((m) => [7, 8, 9].includes(m))) seasons.push("여름");
            if (seasons.length) lines.push(`${seasons.join("·")} 피크 미반영`);
            if (cov?.predictedExcluded) lines.push(`${ymShort(cov.predictedExcluded, true)} 진행 중(제외)`);
            return lines.join("\n");
        },
        notesTitle() {
            const cov = this.coverage;
            const annualized =
                cov && cov.months < cov.total ? ` (최근 12개월 중 ${cov.months}개월 이력으로 연환산)` : "";
            const lines = [
                `추천값은 참고용이며 확정값이 아닙니다${annualized}.`,
                "계약전력 변경은 한전 신청 필요 · 감소 후 1년 내 재증설 제한 · 증설 시 시설부담금 발생 가능",
            ];
            if (this.capacity?.calcType === "PeakLinked")
                lines.push("예상 부담 = 기본요금 + 초과 부가금(150%, 첫 달 예고 제외)");
            return lines.join("\n");
        },
        presetCards() {
            return (this.capacity?.presets || []).map((p) => ({ ...p, label: PRESET_LABELS[p.key] || p.key }));
        },
        penaltyNote() {
            const s = this.selected;
            if (!s || !(s.overrunMonths > 0) || this.capacity?.calcType !== "PeakLinked") return "";
            if (s.annualPenalty > 0) return `초과 부가금 연 ${wonShort(s.annualPenalty)}원 포함`;
            return `재초과 시 부가금 연 최대 ${wonShort(s.annualPenaltyIfRepeat)}원`;
        },
        warnings() {
            const c = this.capacity;
            if (!c || !c.applicable) return [];
            const list = [];
            const monthName = (ym) => `${parseInt(String(ym).substring(4, 6), 10)}월`;
            if (this.outlier) {
                const o = this.outlier;
                const alt = o.alternative;
                const extra = alt ? this.diffOf(alt) - this.diffOf(c.recommended) : null;
                list.push({
                    key: "outlier",
                    level: "warn",
                    text: `${monthName(o.regYm)} ${num(o.value)}kW 이상치 의심`,
                    detail: `다음 최대 ${num(o.secondPeak)}kW의 ${o.ratio}배 · 15분 데이터 확인 필요`,
                    action: alt
                        ? {
                              capacity: alt.capacity,
                              label: `제외 시 ${num(alt.capacity)}kW`,
                              sub:
                                  Number.isFinite(extra) && extra < 0
                                      ? `${this.periodPrefix} ${this.signedWonShort(extra)} 추가`
                                      : "",
                          }
                        : null,
                });
            }
            if (c.recommendedAdjust) {
                list.push({
                    key: "adjust",
                    level: "info",
                    text: `요금제 하한으로 ${num(c.recommended.capacity)}kW 보정`,
                    detail: `계산값 ${num(c.recommendedAdjust.from)}kW · ${c.recommendedAdjust.message}`,
                });
            }
            if (this.selected.tariffChange && c.tariff) {
                const other = c.tariff.grade === "을" ? "갑" : "을";
                list.push({
                    key: "tariff",
                    level: "danger",
                    text: `${c.tariff.className}(${other}) 요금제 변경 필요`,
                    detail: `${c.tariff.className}(을)은 ${num(c.tariff.boundary)}kW 이상, (갑)은 미만 대상`,
                });
            }
            if (this.knee && this.selectedCapacity < this.knee) {
                const refPeak = this.isMonthly && c.refMonthNoSavingBelow ? this.refMonthPeak : null;
                list.push({
                    key: "flat",
                    level: "info",
                    text: `${num(this.knee)}kW 이하 절감 없음`,
                    detail: refPeak
                        ? `${this.refMonthNo}월 적용피크 ${num(refPeak)}kW가 기본요금 기준`
                        : "모든 달 적용피크가 기본요금 기준",
                });
            }
            if (c.nextLever) {
                const hold = c.peakHold;
                list.push({
                    key: "lever",
                    level: "info",
                    text: "추가 절감은 피크 관리 필요",
                    detail: `${num(Math.ceil(c.nextLever.applyPeak / c.minRatio))}kW 이하부터 ${monthName(c.nextLever.sourceYm)} 피크 ${num(c.nextLever.applyPeak)}kW 기준`,
                    value: hold?.validUntilYm ? `피크 ${ymShort(hold.validUntilYm)}까지 유지` : "",
                });
            }
            const order = { danger: 0, warn: 1, info: 2 };
            return list.sort((a, b) => order[a.level] - order[b.level]);
        },
        refMonthPeak() {
            const ref = (this.capacity?.monthly || []).find((m) => String(m.regYm) === String(this.capacity.refYm));
            return ref ? ref.peak : null;
        },
        timeline() {
            const monthly = [...(this.capacity?.monthly || [])].sort((a, b) =>
                String(a.regYm).localeCompare(String(b.regYm)),
            );
            if (!monthly.length) return [];
            const byYm = new Map(monthly.map((m) => [String(m.regYm), m]));
            const last = String(this.capacity.refYm || monthly[monthly.length - 1].regYm);
            const total = this.capacity.windowMonths || 12;
            const lastY = parseInt(last.substring(0, 4), 10);
            const lastM = parseInt(last.substring(4, 6), 10);
            const rows = [];
            for (let i = total - 1; i >= 0; i--) {
                const d = new Date(lastY, lastM - 1 - i, 1);
                const ym = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
                rows.push(byYm.get(ym) || { regYm: ym, missing: true });
            }
            return rows;
        },
        riskAxis() {
            const c = this.capacity;
            const focus = Math.max(c.peakStats?.max || 0, c.recommended?.capacity || 0, this.selectedCapacity || 0);
            const currentOutOfRange = c.currentCapacity > focus * 1.5;
            const top = Math.max(focus, currentOutOfRange ? 0 : c.currentCapacity) * 1.15;
            const interval = niceCeil(top / 5);
            return { max: Math.ceil(top / interval) * interval, interval, currentOutOfRange };
        },
        riskOption() {
            const c = this.capacity;
            const months = this.timeline;
            const whatIf = this.selectedCapacity;
            const categories = months.map((m) => `${parseInt(String(m.regYm).substring(4, 6), 10)}월`);
            const outlierYm = this.outlier ? String(this.outlier.regYm) : null;
            const flat = (value) => months.map(() => value);

            const { max: axisMax, interval, currentOutOfRange } = this.riskAxis;

            const series = [
                {
                    name: "월 최대수요",
                    type: "bar",
                    barMaxWidth: 60,
                    barCategoryGap: "25%",
                    itemStyle: { color: barGradient(DEMAND_COLOR, 0.35) },
                    data: months.map((m) => {
                        if (m.missing) return { value: null };
                        const isOutlier = String(m.regYm) === outlierYm;
                        return {
                            value: m.maxDemand,
                            itemStyle: {
                                color: barGradient(m.maxDemand > whatIf ? chartColors.danger : DEMAND_COLOR, 0.35),
                                opacity: m.predicted ? 0.5 : 1,
                                borderRadius: [4, 4, 0, 0],
                            },
                            label: isOutlier
                                ? {
                                      show: true,
                                      position: "insideTop",
                                      distance: 6,
                                      formatter: "이상치",
                                      color: chartColors.warn,
                                      backgroundColor: "#fff",
                                      padding: [2, 5],
                                      borderRadius: 3,
                                      fontSize: 10,
                                      fontWeight: 700,
                                  }
                                : { show: false },
                        };
                    }),
                    markArea: {
                        silent: true,
                        itemStyle: { color: "rgba(208, 217, 226, 0.12)" },
                        label: {
                            show: true,
                            position: "insideBottom",
                            color: grayScale[3],
                            fontSize: 10,
                            formatter: "없음",
                        },
                        data: months.map((m, i) => (m.missing ? [{ xAxis: i }, { xAxis: i }] : null)).filter(Boolean),
                    },
                },
                ...(currentOutOfRange
                    ? []
                    : [
                          {
                              name: "현재 계약전력",
                              type: "line",
                              symbol: "none",
                              lineStyle: { width: 1.5, type: "dashed", color: CURRENT_COLOR },
                              itemStyle: { color: CURRENT_COLOR },
                              data: flat(c.currentCapacity),
                          },
                      ]),
                {
                    name: "선택 계약전력",
                    type: "line",
                    symbol: "none",
                    lineStyle: { width: 2, color: SELECTED_COLOR },
                    itemStyle: { color: SELECTED_COLOR },
                    data: flat(whatIf),
                },
            ];
            return {
                backgroundColor: "transparent",
                animationDurationUpdate: 200,
                grid: { left: 8, right: 8, top: 30, bottom: 4, containLabel: true },
                legend: { top: 0, left: 0, itemWidth: 12, itemHeight: 6, textStyle: legendText() },
                tooltip: tooltipStyle({
                    trigger: "axis",
                    appendTo: "body",
                    formatter: (params) => {
                        const m = months[params[0]?.dataIndex];
                        if (!m) return "";
                        let html =
                            tooltipHtml.title(`${ymLabel(m.regYm)}${m.predicted ? " (진행 중)" : ""}`) +
                            tooltipHtml.bodyOpen();
                        if (m.missing)
                            return html + tooltipHtml.row("", "최대수요", "데이터 없음") + tooltipHtml.bodyClose();
                        const marker = (name) => params.find((p) => p.seriesName === name)?.marker || "";
                        html += tooltipHtml.row(marker("월 최대수요"), "월 최대수요", `${num(m.maxDemand)} kW`);
                        html += tooltipHtml.row(
                            marker("현재 계약전력"),
                            "현재 계약전력",
                            `${num(c.currentCapacity)} kW`,
                        );
                        html += tooltipHtml.row(marker("선택 계약전력"), "선택 계약전력", `${num(whatIf)} kW`);
                        if (m.maxDemand > whatIf)
                            html += tooltipHtml.row(
                                "",
                                "초과",
                                `${num(m.maxDemand - whatIf)} kW (${pct(((m.maxDemand - whatIf) / whatIf) * 100)})`,
                            );
                        return html + tooltipHtml.bodyClose();
                    },
                }),
                xAxis: {
                    type: "category",
                    data: categories,
                    axisLabel: axisLabel(),
                    axisLine: axisLine(),
                    axisTick: { show: false },
                },
                yAxis: {
                    type: "value",
                    max: axisMax,
                    interval,
                    axisLabel: axisLabel({ formatter: (v) => `${num(v)}kW` }),
                    splitLine: splitLine(),
                },
                graphic: currentOutOfRange
                    ? [
                          {
                              type: "group",
                              right: 8,
                              top: 0,
                              children: [
                                  {
                                      type: "text",
                                      x: 0,
                                      y: 1,
                                      style: {
                                          text: `현재 계약전력(${num(c.currentCapacity)}kW) ↑`,
                                          fill: grayScale[0],
                                          font: "10px sans-serif",
                                      },
                                  },
                              ],
                          },
                      ]
                    : [],
                series,
            };
        },
        curveOption() {
            const c = this.capacity;
            const list = this.scenarios;
            const minX = list[0]?.capacity || 0;
            const maxX = list[list.length - 1]?.capacity || 0;
            const inRange = (v) => Number.isFinite(v) && v >= minX && v <= maxX;
            const vLine = (value, label, color, type = "solid") => ({
                xAxis: value,
                lineStyle: { color, type, width: 1.5 },
                label: { formatter: label, color, fontSize: 10, fontWeight: 700, position: "insideEndTop" },
            });
            const marks = [];
            if (inRange(c.currentCapacity)) marks.push(vLine(c.currentCapacity, "현재", CURRENT_COLOR, "dashed"));
            if (this.knee && inRange(this.knee) && this.knee > minX)
                marks.push(vLine(this.knee, "절감 하한", grayScale[2], "dotted"));
            if (this.tariffBoundary && inRange(this.tariffBoundary) && this.tariffBoundary > minX)
                marks.push(vLine(this.tariffBoundary, "요금제 경계", chartColors.warn, "dotted"));
            marks.push(vLine(this.selectedCapacity, "선택", SELECTED_COLOR));
            const hasPenalty = list.some((s) => s.annualPenalty > 0);

            return {
                backgroundColor: "transparent",
                animationDurationUpdate: 200,
                grid: { left: 8, right: 16, top: 30, bottom: 4, containLabel: true },
                legend: { top: 0, left: 0, itemWidth: 12, itemHeight: 6, textStyle: legendText() },
                tooltip: tooltipStyle({
                    trigger: "axis",
                    appendTo: "body",
                    formatter: (params) => {
                        const s = list[params.find((p) => p.seriesName === "연 기본요금")?.dataIndex];
                        if (!s) return "";
                        let html = tooltipHtml.title(`계약전력 ${num(s.capacity)} kW`) + tooltipHtml.bodyOpen();
                        html += tooltipHtml.row("", "연 기본요금", `${num(s.annualBaseFee)}원`);
                        if (c.calcType === "PeakLinked")
                            html += tooltipHtml.row("", "초과사용부가금", `${num(s.annualPenalty)}원`);
                        html += tooltipHtml.row("", "연 예상 부담", `${num(s.annualCost)}원`);
                        html += tooltipHtml.row("", "현재 대비", this.signedWonShort(-s.annualSaving));
                        if (s.overrunMonths > 0) html += tooltipHtml.row("", "초과 위험", `${s.overrunMonths}개월`);
                        if (s.tariffChange) html += tooltipHtml.row("", "요금제", "변경 필요");
                        return html + tooltipHtml.bodyClose();
                    },
                }),
                xAxis: {
                    type: "value",
                    min: minX,
                    max: maxX,
                    axisLabel: axisLabel({ formatter: (v) => `${num(v)}kW` }),
                    axisLine: axisLine(),
                    splitLine: { show: false },
                },
                yAxis: {
                    type: "value",
                    min: 0,
                    axisLabel: axisLabel({ formatter: (v) => (v ? wonShort(v) : "0") }),
                    splitLine: splitLine(),
                },
                series: [
                    {
                        name: "연 기본요금",
                        type: "line",
                        stack: "cost",
                        symbol: "none",
                        lineStyle: { width: 2, color: DEMAND_COLOR },
                        itemStyle: { color: DEMAND_COLOR },
                        areaStyle: { color: barGradient(DEMAND_COLOR, 0.05), opacity: 0.2 },
                        data: list.map((s) => [s.capacity, s.annualBaseFee]),
                        markLine: { silent: true, symbol: "none", data: marks },
                    },
                    ...(hasPenalty
                        ? [
                              {
                                  name: "초과사용부가금",
                                  type: "line",
                                  stack: "cost",
                                  symbol: "none",
                                  lineStyle: { width: 0 },
                                  itemStyle: { color: chartColors.danger },
                                  areaStyle: { color: PENALTY_COLOR },
                                  data: list.map((s) => [s.capacity, s.annualPenalty]),
                              },
                          ]
                        : []),
                    {
                        name: "선택지",
                        type: "scatter",
                        symbolSize: 9,
                        cursor: "pointer",
                        itemStyle: { color: "#fff", borderColor: SELECTED_COLOR, borderWidth: 2 },
                        label: {
                            show: true,
                            position: "top",
                            formatter: (p) => p.data.label,
                            color: grayScale[0],
                            fontSize: 10,
                            fontWeight: 700,
                        },
                        data: this.presetCards
                            .filter((p) => inRange(p.capacity))
                            .map((p) => ({ value: [p.capacity, p.annualCost], label: p.label })),
                    },
                ],
            };
        },
    },
    watch: {
        capacity: {
            immediate: true,
            handler() {
                this.resetToRecommended();
            },
        },
    },
    methods: {
        num,
        pct,
        wonShort,
        tip(text) {
            const escape = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
            const title = String(text || "")
                .split("\n")
                .filter(Boolean)
                .map((line) => `<div>${escape(line)}</div>`)
                .join("");
            return { title, html: true, placement: "bottomleft", customClass: "ebc-tooltip" };
        },
        resetToRecommended() {
            this.selectCapacity(this.capacity?.recommended?.capacity);
        },
        selectCapacity(target) {
            const index = this.scenarios.findIndex((s) => s.capacity === target);
            this.selectedIndex = index >= 0 ? index : 0;
        },
        onChartClick(params) {
            if (this.chartView === "curve" && params.seriesName === "선택지") this.selectCapacity(params.value[0]);
        },
        riskOf(scenario) {
            if (scenario.tariffChange) return "요금제 변경";
            if (scenario.overrunMonths > 0) return `초과 ${scenario.overrunMonths}개월`;
            return "";
        },
        signedWonShort(value) {
            if (!Number.isFinite(Number(value))) return "-";
            const n = Number(value);
            return `${n > 0 ? "+" : n < 0 ? "-" : ""}${wonShort(Math.abs(n))}원`;
        },
        feeOf(scenario) {
            if (!scenario) return null;
            if (!this.isMonthly) return scenario.annualCost;
            if (this.hasRefMonthFee) return scenario.refMonthCost;
            return Number.isFinite(scenario.annualCost) ? Math.floor(scenario.annualCost / 12) : null;
        },
        diffOf(scenario) {
            const fee = this.feeOf(scenario);
            const base = this.feeOf(this.capacity?.current);
            return Number.isFinite(fee) && Number.isFinite(base) ? fee - base : null;
        },
        rateOf(scenario) {
            const base = this.feeOf(this.capacity?.current);
            const diff = this.diffOf(scenario);
            return base > 0 && Number.isFinite(diff) ? (diff / base) * 100 : null;
        },
        signedPct(value) {
            if (!Number.isFinite(value)) return "-";
            return `${value > 0 ? "+" : value < 0 ? "-" : ""}${pct(Math.abs(value))}`;
        },
        diffClass(diff) {
            if (!diff) return "";
            return diff < 0 ? "is-good" : "is-bad";
        },
    },
};
</script>

<style scoped>
.capacity-body {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 480px;
    gap: 16px;
}

.capacity-chart {
    min-width: 0;
    min-height: 0;
}

.capacity-badges {
    display: flex;
    align-items: center;
    gap: 6px;
}

.capacity-panel .ebc-panel__head > div:first-child {
    align-items: center;
}

.capacity-panel .ebc-panel__title {
    line-height: 1;
}

.capacity-views {
    margin-left: 4px;
}

.capacity-views .ebc-segmented__item {
    padding: 3px 10px;
    font-size: 11px;
}

.capacity-info {
    font-size: 13px;
    color: var(--ebc-text-muted);
    cursor: help;
}

.sim-card {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 0;
    padding: 10px 14px;
    overflow-y: auto;
    border-radius: var(--ebc-radius-sm);
    background: var(--ebc-primary-tint);
}

.preset-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 6px;
}

.preset {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1px;
    min-width: 0;
    padding: 6px 8px;
    text-align: left;
    background: #fff;
    border: 1px solid transparent;
    border-radius: var(--ebc-radius-sm);
    cursor: pointer;
    transition: border-color 0.15s;
}

.preset:focus {
    outline: none;
}

.preset:hover:not(.is-active) {
    border-color: var(--ebc-primary-tint-strong);
}

/* inset box-shadow 는 html2canvas(PDF)에서 두껍게 그려져 2px 테두리로 표현한다 */
.preset.is-active {
    padding: 5px 7px;
    border: 2px solid var(--ebc-primary);
}

.preset__label {
    font-size: 10px;
    font-weight: 600;
    color: var(--ebc-text-sub);
    white-space: nowrap;
}

.preset__tag {
    margin-left: 4px;
    padding: 0 5px;
    font-size: 9px;
    font-style: normal;
    font-weight: 700;
    line-height: 14px;
    color: #fff;
    border-radius: var(--ebc-radius-pill);
    background: var(--ebc-primary);
}

.preset__value {
    font-size: 16px;
    font-weight: 800;
    line-height: 1.2;
    color: var(--ebc-text);
    letter-spacing: -0.02em;
}

.preset__value small,
.sim-metrics dd small {
    margin-left: 1px;
    font-size: 10px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.preset__diff {
    font-size: 11px;
    font-weight: 700;
    color: var(--ebc-text-sub);
    white-space: nowrap;
}

.preset__risk {
    font-size: 10px;
    font-weight: 600;
    color: var(--ebc-danger);
    white-space: nowrap;
}

.sim-metrics {
    display: grid;
    grid-template-columns: 1.2fr 1.4fr 1fr;
    gap: 10px;
    margin: 0;
    padding-top: 8px;
    font-size: 11px;
    border-top: 1px solid var(--ebc-primary-tint-strong);
}

.sim-metrics div {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.sim-metrics dt {
    font-weight: 500;
    color: var(--ebc-text-sub);
    white-space: nowrap;
}

.sim-metrics dd {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: var(--ebc-text);
    white-space: nowrap;
}

.sim-metrics dd.is-primary {
    color: var(--ebc-primary);
}

.sim-metrics__note {
    grid-column: 1 / -1;
    margin: -4px 0 0;
    font-size: 10px;
    color: var(--ebc-danger);
}

.sim-rate {
    font-size: 11px;
    font-weight: 600;
}

.is-good {
    color: var(--ebc-positive) !important;
}

.is-bad {
    color: var(--ebc-danger) !important;
}

.sim-insights {
    display: grid;
    gap: 4px;
    margin: 0;
    padding: 0;
    list-style: none;
}

.sim-insights li {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    padding: 5px 10px;
    border-radius: var(--ebc-radius-sm);
    background: #fff;
}

.sim-insights__level {
    display: inline-block;
    margin-right: 6px;
    padding: 0 6px;
    font-size: 10px;
    font-style: normal;
    font-weight: 700;
    line-height: 16px;
    vertical-align: 1px;
    border-radius: 4px;
}

.sim-insights li.is-warn .sim-insights__level {
    color: var(--ebc-warn);
    background: var(--ebc-warn-tint);
}

.sim-insights li.is-danger .sim-insights__level {
    color: var(--ebc-danger);
    background: rgba(194, 91, 91, 0.12);
}

.sim-insights__body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
}

.sim-insights__body strong {
    font-size: 11.5px;
    line-height: 16px;
    font-weight: 700;
    color: var(--ebc-text);
}

.sim-insights__body strong,
.sim-insights__body span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.sim-insights__body span {
    font-size: 10.5px;
    line-height: 14px;
    color: var(--ebc-text-sub);
}

.sim-insights__value {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.sim-insights__action {
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
    align-items: flex-end;
    gap: 0;
    padding: 3px 8px;
    color: var(--ebc-primary);
    background: var(--ebc-primary-tint);
    border: 0;
    border-radius: var(--ebc-radius-sm);
    cursor: pointer;
}

.sim-insights__action:hover {
    background: var(--ebc-primary-tint-strong);
}

.sim-insights__action:focus {
    outline: none;
}

.sim-insights__action b {
    font-size: 11px;
    font-weight: 700;
}

.sim-insights__action small {
    font-size: 10px;
    font-weight: 600;
    color: var(--ebc-positive);
}
</style>
